import Core from "@/core/Core";
import Partner from "@/domains/Partners/Partner";
import Company from "@/domains/Companies/Company";
import DeliveryType from "@/domains/DeliveryTypes/DeliveryType";
import ContractsExternalDAO from "@/domains/Contracts/ContractsExternalDAO";
import ContractExternal from "@/domains/Contracts/ContractExternal";
import PartnersService from "@/domains/Partners/PartnersService";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";

/**
 * Сервис для работы с внешними договорами
 */

class ContractsExternalService {

	static async get(id: number): Promise<ContractExternal | null> {
		const response = await ContractsExternalDAO.get(id);

		return Core.utils.hydrate(response.data, ContractExternal.fromServer);
	}

	static async getList(
    params?: Parameters<typeof ContractsExternalDAO.getList>[0]
	): Promise<ContractExternal[]> {
		const response = await this.getListPaginated(params);
		return response.data;
	}

	/**
	 * Пагинированная версия для DataGridLoader. Принимает фильтры от грида
	 * и возвращает данные в форме { data, pagination }.
	 *
	 * Бэкенд для contracts.getListExternal сейчас не поддерживает пагинацию
	 * и сортировку, поэтому соответствующие параметры игнорируются.
	 */
	static async getListPaginated(
    params?: Parameters<typeof ContractsExternalDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    }
  ): Promise<{ data: ContractExternal[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await ContractsExternalDAO.getList(filters, pagination, sort);

		return {
      data: Core.utils.hydrateArray(response.data, ContractExternal.fromServer),
      pagination: response.meta.pagination,
    };
	}

	/** ------- CRUD ------- */

	static async add(contract: ContractExternal): Promise<boolean> {
		const response = await ContractsExternalDAO.add(contract);

		if (response.ok) {
			Core.showNotification.success('Внешний договор успешно добавлен');
		}

		return response.ok;
	}

	static async update(contract: ContractExternal): Promise<boolean> {
		const response = await ContractsExternalDAO.update(contract);

		if (response.ok) {
			Core.showNotification.success('Внешний договор успешно обновлен');
		}

		return response.ok;
	}

	static async delete(id: number, changeReasonText: string): Promise<boolean> {
		const response = await ContractsExternalDAO.delete(id, changeReasonText);

		if (response.ok) {
			Core.showNotification.success('Внешний договор успешно удален');
		}

		return response.ok;
	}

	/** ------- открытие диалога редактирования и создания контракта ------- */

	static async createContract(
		partner: Partner,
		onContractCreated: (data?: any) => void
	): Promise<void> {

		const contractFromPartner = ContractExternal.fromPartner(partner);

		contractFromPartner.buyer = Core.currentUser!.partner;

		if (contractFromPartner.buyer && Core.currentUser?.partner.companies[0]) {
			contractFromPartner.buyerCompany = Core.currentUser?.partner.companies[0];
		}

		return this.showContractDialog(contractFromPartner, onContractCreated, undefined);
	}

	// перегрузки
	static async editContract(
		contractId: number,
		onContractUpdated: (data?: any) => void,
		onBeforeUnmount?: (data?: any) => void,
	): Promise<void>;
	static async editContract(
		contract: ContractExternal,
		onContractUpdated: (data?: any) => void,
		onBeforeUnmount?: (data?: any) => void,
	): Promise<void>;

	// реализация
	static async editContract(
		contractOrId: ContractExternal | number,
		onContractUpdated: (data?: any) => void,
		onBeforeUnmount?: (data?: any) => void,
	): Promise<void> {
		let contract: ContractExternal;

		// Если передан ID - загружаем сущность с сервера
		if (typeof contractOrId === 'number') {
			contract = await this.get(contractOrId);
		} else {
			// Если передан объект - используем его
			contract = ContractExternal.fromServer(contractOrId);
		}

		if (!contract) return;

		this.showContractDialog(contract,
			null,
			onContractUpdated,
			onBeforeUnmount,
		);
	}

	static showContractDialog(
		contract: ContractExternal,
		onContractCreated: (data?: any) => void,
		onContractUpdated: (data?: any) => void,
		onBeforeUnmount?: (data?: any) => void,
	): Promise<void> {
		return Core.dialogs.ContractExternalCard.showAsPromise(
			{
				id: contract.id,
				contract
			},
			{
				[ContractExternal.Events.Created]: onContractCreated,
				[ContractExternal.Events.Updated]: onContractUpdated,
				'component:before-unmount': onBeforeUnmount,
			}
		);
	}

	/**
	 * Открывает ленту истории/комментариев договора
	 */

	static showFeed(contractId: number): void {
		Core.dialogs.FeedDialog.show({
			entityTypeName: 'contractExternal',
			entityId:       contractId,
			showAddComment: false,
		});
	}

	/**
	 * Удаляет договор с подтверждением и указанием причины.
	 * Возвращает true если удаление произошло.
	 */

	static async deleteWithConfirm(contract: ContractExternal): Promise<boolean> {
		const { isApproved, reasonText } = await Core.dialogs.confirmWithReason(
			'Удалить этот договор?',
			`Договор №${contract.number}`,
		);

		if (!isApproved) return false;

		return await this.delete(contract.id, reasonText);
	}

	// ============================================================
	// Контекстное меню
	// ============================================================

	/**
	 * Пункты контекстного меню договора. Один источник и для грида реестра
	 * (VDataGridV2 :context-menu), и для карточки (VDialogV3 :context-menu).
	 * Геттер, а не статика: callback'и завязаны на сервис.
	 */

	static get contextMenu(): ContextMenuItem[] {
		return [
			new ContextMenuItem(
				'Открыть',
				'open_in_new',
				(contract: ContractExternal) => { return this.editContract(contract, null); },
				null,
				false,
				true,
				true,
			),
			new ContextMenuItem(
				'Перейти к поставщику',
				'person',
				(contract: ContractExternal) => { return PartnersService.showPartnerCard(contract.seller.id); },
				null,
				false,
				false,
			),
			new ContextMenuItem(
				'История',
				'history',
				(contract: ContractExternal) => { this.showFeed(contract.id); },
				null,
				false,
				false,
			),
			new ContextMenuItem(
				'Удалить',
				'delete',
				(contract: ContractExternal) => { return this.deleteWithConfirm(contract); },
				(contract: ContractExternal) => contract.isDeleted ? 'Договор уже удалён' : null,
			),
		];
	}
}

export default ContractsExternalService;
